import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { MUSIC_VIDEO_LYRIC_STYLES } from 'fym-shared/src/music24/musicVideoLook';
import type { MusicVideoLyrics } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { catalogSongPath } from '../../smartLinks/catalog/song/page/catalogSongPath';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { lyricStyleMessages, panelMessages } from '../messages/panelMessages';
import { LyricLines } from './LyricLines';
import { Segmented } from './Segmented';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STATUS_MESSAGES = {
  synced: panelMessages.statusSynced,
  syncing: panelMessages.statusSyncing,
  failed: panelMessages.statusFailed,
  none: panelMessages.statusNone,
} as const;

/**
 * The Lyrics panel (design d1): the captions on or off, the four caption styles, where and when they
 * show, and the lines with their timing. The words and the times belong to the song: "Edit the
 * timing" opens the Lyrics section of the song page.
 */
const LyricsPanelComponent: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { doc, data, change, readOnly, accountId, catalogItemUuid, mode } = useMusicVideoEditor();
  // The showcase song has no song page: the links to it stay out.
  const hasSongPage = mode === 'account';
  const { lyrics } = doc;
  const patch = (next: Partial<MusicVideoLyrics>) =>
    change((current) => ({ ...current, lyrics: { ...current.lyrics, ...next } }));
  const hasLines = data.lyrics.lines.length > 0;
  const openSong = (view?: 'text') => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(catalogSongPath(catalogItemUuid, { section: 'lyrics', accountId, view }));
  };

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(panelMessages.lyricsTitle)}</h3>
      {!hasLines ? (
        <>
          <p className={styles.hint}>{intl.formatMessage(panelMessages.noLyrics)}</p>
          {hasSongPage && (
            <button type="button" className={styles.ghostButton} onClick={() => openSong('text')}>
              {intl.formatMessage(panelMessages.addLyrics)}
            </button>
          )}
        </>
      ) : (
        <>
          <div className={styles.row}>
            <button
              type="button"
              role="switch"
              aria-checked={lyrics.on}
              aria-label={intl.formatMessage(panelMessages.lyricsShow)}
              disabled={readOnly}
              className={clsx(styles.toggle, lyrics.on && styles.toggleOn)}
              onClick={() => patch({ on: !lyrics.on })}
            />
            <b>{intl.formatMessage(panelMessages.lyricsShow)}</b>
            <span className={styles.grow} />
            <span className={styles.hint}>
              {intl.formatMessage(panelMessages.lyricsFacts, {
                status: intl.formatMessage(STATUS_MESSAGES[data.lyrics.status]),
                count: data.lyrics.lines.length,
              })}
            </span>
          </div>
          {data.lyrics.status === 'syncing' && (
            <p className={styles.alertInfo}>{intl.formatMessage(panelMessages.syncingNote)}</p>
          )}
          <div className={styles.captionGrid}>
            {MUSIC_VIDEO_LYRIC_STYLES.map((style) => (
              <button
                key={style}
                type="button"
                className={clsx(styles.captionTile, style === lyrics.style && styles.captionTileOn)}
                aria-pressed={style === lyrics.style}
                disabled={readOnly}
                onClick={() => patch({ style, on: true })}
              >
                <span
                  className={styles.captionSample}
                  style={
                    data.song.artworkUrl === null ? undefined : { backgroundImage: `url("${data.song.artworkUrl}")` }
                  }
                >
                  <span
                    className={
                      style === 'caps' ? styles.captionCaps : style === 'serif' ? styles.captionSerif : undefined
                    }
                  >
                    {intl.formatMessage(panelMessages.sample)}
                  </span>
                </span>
                {intl.formatMessage(lyricStyleMessages[style])}
              </button>
            ))}
          </div>
          <div className={styles.inspectorGrid}>
            <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.where)}</span>
            <Segmented
              label={intl.formatMessage(panelMessages.where)}
              value={lyrics.where}
              disabled={readOnly}
              options={[
                { value: 'bottom', label: intl.formatMessage(panelMessages.bottom) },
                { value: 'middle', label: intl.formatMessage(panelMessages.middle) },
              ]}
              onChange={(where) => patch({ where })}
            />
            <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.when)}</span>
            <Segmented
              label={intl.formatMessage(panelMessages.when)}
              value={lyrics.when}
              disabled={readOnly}
              options={[
                { value: 'all', label: intl.formatMessage(panelMessages.whenAll) },
                { value: 'choruses', label: intl.formatMessage(panelMessages.whenChoruses) },
              ]}
              onChange={(when) => patch({ when })}
            />
          </div>
          <div className={styles.separator} />
          <LyricLines />
          <span className={styles.grow} />
          <p className={styles.hint}>
            {intl.formatMessage(panelMessages.lyricsHint)}
            {hasSongPage && (
              <>
                {' '}
                <button type="button" className={styles.linkButton} onClick={() => openSong()}>
                  {intl.formatMessage(panelMessages.editTiming)}
                </button>
              </>
            )}
          </p>
        </>
      )}
    </>
  );
};

export const LyricsPanel = TypedMemo(LyricsPanelComponent);
